import { useEffect, useMemo, useRef } from 'react';
import { capitalize, isNil } from 'lodash';
import { useParams, useNavigate, NavLink } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import { useTitle } from '@/lib/hooks/use-title';
import { useAccountContext, useBuildAccountUrl } from '@/lib/accounts/context';
import { Loader } from '@/components/loader';
import { useToastContext } from '@/components/toast';
import {
  useOauthSlackCallbackMutation,
  OauthSlackCallbackMutation,
  OauthSlackCallbackSuccess,
  OauthSlackCallbackError,
} from '@/graphql';

import { SlackIntegrationKey } from '../utils';

import settingsStyles from '../../settings.module.scss';
import styles from './../integrations.module.scss';

const isSuccessful = (
  data?: OauthSlackCallbackMutation | null,
): data is { oauthSlackCallback: OauthSlackCallbackSuccess } =>
  data?.oauthSlackCallback?.__typename === 'OauthSlackCallbackSuccess';

export const IntegrationCallbackPage = () => {
  useTitle('Integrating...');
  const { account } = useAccountContext();
  const addToast = useToastContext();
  const { integration } = useParams();
  const searchParams = useMemo(() => {
    return new URLSearchParams(window.location.search);
  }, []);
  const navigate = useNavigate();
  const buildAccountUrl = useBuildAccountUrl();
  const settingsLink = buildAccountUrl('/settings/integrations');
  const hasProcessed = useRef(false);
  const [slackState, _, removeSlackState] = useLocalStorage(SlackIntegrationKey, null);

  const [slackCallback, { loading, data }] = useOauthSlackCallbackMutation();

  const handleError = (error: string) => {
    addToast({
      title: 'Error',
      content: () => `Connecting Slack failed: ${error}`,
      kind: 'error',
    });
    return navigate(settingsLink);
  };

  useEffect(() => {
    if (hasProcessed.current) {
      return;
    }

    removeSlackState();
    hasProcessed.current = true;

    if (
      slackState === undefined ||
      searchParams.get('state') !== slackState ||
      isNil(searchParams.get('code'))
    ) {
      console.error('Faulty state or missing code', {
        state: slackState,
        code: searchParams.get('code'),
      });
      const error = searchParams.get('error');
      handleError(error ?? '');
    }

    async function exchangeCode() {
      const res = await slackCallback({
        variables: {
          accountId: account.accountId,
          code: searchParams.get('code') ?? '',
          redirectUri: `https://${location.host}${buildAccountUrl('/settings/integrate/slack')}`,
        },
        refetchQueries: ['AccountSettings'],
        awaitRefetchQueries: true,
      });

      if (isSuccessful(res.data)) {
        addToast({
          title: 'Slack integrated',
          content: () => 'Your Slack workspace is now connected',
          kind: 'success',
        });
        return navigate(settingsLink);
      }

      const errorResponse = res.data?.oauthSlackCallback as OauthSlackCallbackError;
      handleError(errorResponse.error ?? 'Unknown error');
    }

    exchangeCode();
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const successful = useMemo(() => isSuccessful(data), [data]);

  return (
    <div className={settingsStyles.wrapper}>
      <h1 className={styles.title}>Integrating {capitalize(integration)}</h1>
      <div className={styles.status}>
        {loading ? (
          <Loader />
        ) : (
          <>
            {successful ? <p>Slack connected!</p> : <p>Error: {searchParams.get('error')}</p>}
            <NavLink to={settingsLink}>Back to settings</NavLink>
          </>
        )}
      </div>
    </div>
  );
};
